import React, { useState, useEffect, useRef } from 'react';
// #546: Kein Icon namens "File" importieren -- es ueberdeckt den Browser-Konstruktor,
// den handlePasteFromClipboardEvent fuer eingefuegten Text braucht.
import apiService from '../../services/apiService';
import Modal from '../ui/Modal';
import AnhangViewer from '../ui/AnhangViewer';
import { anhangArt, ticketAnhangUrl } from '../../utils/anhangArt';
import { useI18n } from '../../utils/i18n';
import { useConfirm } from '../ui/ConfirmDialog';

// #530 (Scheibe 5 aus #310): Anhaenge in TicketDetail -- Dialog zum
// Hochladen, Einfuegen aus der Zwischenablage (global, solange kein
// blockierender Dialog offen ist), Ziehen und Ablegen, Vorschaubilder per
// IntersectionObserver, Bildansicht mit Blaettern, Herunterladen und
// Loeschen. Code unveraendert aus TicketDetail.js uebernommen, Muster aus
// #312/#471/#528.
//
// Den Hook ruft TicketDetail auf, die Dialoge rendern nur. Die fuenf
// Dialog-Zustaende kommen herein, weil Paste- und Drag-Sperre sie lesen.
export function useAnhaenge({
  ticket, currentTicket, history, historyListEl, visibleHistoryCount, setError, setHasChanges, loadTicketHistory,
  showAddCommentModal, showEditModal, showEditCommentModal, showTransferModal, showCompleteModal
}) {
  const { t } = useI18n();
  const confirm = useConfirm();
  // Attachment states
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const justClosedAtRef = useRef(0);
  const thumbnailUrlsRef = useRef({});
  const thumbnailRequestedRef = useRef(new Set());
  const thumbnailObserverRef = useRef(null);

  // Keep latest thumbnail URLs in a ref for cleanup
  useEffect(() => {
    thumbnailUrlsRef.current = thumbnailUrls;
  }, [thumbnailUrls]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      try {
        Object.values(thumbnailUrlsRef.current).forEach(url => {
          if (typeof url === 'string' && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      } catch (_) {}
    };
  }, []);

  // IntersectionObserver für Thumbnail-Lazy-Loading
  useEffect(() => {
    try {
      if (thumbnailObserverRef.current) {
        try { thumbnailObserverRef.current.disconnect(); } catch (_) {}
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          try {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            observer.unobserve(el);
            const attId = el.getAttribute('data-attachment-id');
            const ticketId = el.getAttribute('data-ticket-id') || ticket.id;
            if (!attId) return;
            if (thumbnailUrlsRef.current[attId]) return;
            if (thumbnailRequestedRef.current.has(attId)) return;
            thumbnailRequestedRef.current.add(attId);
            (async () => {
              try {
                const blob = await apiService.getTicketAttachmentThumbnail(ticketId, attId);
                if (!blob || blob.size === 0) return;
                const url = URL.createObjectURL(blob);
                setThumbnailUrls(prev => {
                  const prevUrl = prev[attId];
                  if (prevUrl && typeof prevUrl === 'string' && prevUrl.startsWith('blob:')) {
                    try { URL.revokeObjectURL(prevUrl); } catch (_) {}
                  }
                  return { ...prev, [attId]: url };
                });
              } catch (error) {
                console.error('📎 [THUMBNAIL LAZY] Fehler beim Laden des Thumbnails', { attId, error });
              }
            })();
          } catch (_) {}
        });
      }, { root: null, rootMargin: '200px 0px', threshold: 0.01 });
      thumbnailObserverRef.current = observer;
      const scope = historyListEl || document;
      const nodes = scope.querySelectorAll('.attachment-thumbnail[data-attachment-id]');
      nodes.forEach(el => {
        try {
          const attId = el.getAttribute('data-attachment-id');
          if (!attId) return;
          if (thumbnailUrlsRef.current[attId]) return;
          if (thumbnailRequestedRef.current.has(attId)) return;
          observer.observe(el);
        } catch (_) {}
      });
      return () => {
        try { observer.disconnect(); } catch (_) {}
      };
    } catch (_) {}
  }, [history, visibleHistoryCount, historyListEl]);

  // (ehemals: loadThumbnails) — entfernt zugunsten von Lazy-Loading via IntersectionObserver

  // Flache Liste aller Bild- und PDF-Anhänge der Historie für den Betrachter (#636)
  useEffect(() => {
    try {
      const images = [];
      for (const item of history) {
        const attachments = item.attachments || (item.attachment ? [item.attachment] : []);
        for (const att of attachments) {
          if (att && anhangArt(att)) {
            images.push({
              ticketId: att.ticketId || currentTicket.id,
              attachmentId: att.id,
              originalName: att.originalName || att.filename || 'Bild',
              contentType: att.contentType
            });
          }
        }
      }
      setImageList(images);
    } catch (_) {}
  }, [history, currentTicket.id]);

  // ================ ATTACHMENT HANDLERS ================

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let hasErrors = false;

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { // 10MB
        setAttachmentError(t('ticketDetail.fileTooLarge', 'Datei "{{name}}" ist zu groß. Maximum sind 10MB.', { name: file.name }));
        hasErrors = true;
        break;
      }
      validFiles.push(file);
    }

    if (!hasErrors) {
      setSelectedFiles(validFiles);
      setAttachmentError(null);
    }
  };

  const handlePasteFromClipboardEvent = async (e) => {
    try {
      // Disable attachment paste while certain modals are open
      if (showAddCommentModal || showEditModal || showEditCommentModal || showTransferModal || showCompleteModal) return;
      if (!e || !e.clipboardData) return;
      setAttachmentError(null);

      const newFiles = [];
      const filesList = Array.from(e.clipboardData.files || []);
      const items = Array.from(e.clipboardData.items || []);

      // 1) Zuerst direkte Files nehmen (enthält Screenshots)
      for (const f of filesList) {
        if (!f) continue;
        if (f.size > 10 * 1024 * 1024) {
          setAttachmentError(t('ticketDetail.fileTooLarge', 'Datei "{{name}}" ist zu groß. Maximum sind 10MB.', { name: f.name || 'clipboard' }));
          continue;
        }
        newFiles.push(f);
      }

      // 2) Danach items: file und string
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (!file) continue;
          if (file.size > 10 * 1024 * 1024) {
            setAttachmentError(t('ticketDetail.fileTooLarge', 'Datei "{{name}}" ist zu groß. Maximum sind 10MB.', { name: file.name || 'clipboard' }));
            continue;
          }
          newFiles.push(file);
        } else if (item.kind === 'string') {
          try {
            const text = await new Promise(resolve => item.getAsString(resolve));
            if (text && text.trim().length > 0) {
              const blob = new Blob([text], { type: 'text/plain' });
              if (blob.size <= 10 * 1024 * 1024) {
                newFiles.push(new File([blob], 'clipboard.txt', { type: 'text/plain' }));
              } else {
                setAttachmentError(t('ticketDetail.fileTooLarge', 'Datei "{{name}}" ist zu groß. Maximum sind 10MB.', { name: 'clipboard.txt' }));
              }
            }
          } catch (_) {}
        }
      }

      // Dedup nach name+size+type
      const seen = new Set();
      const deduped = [];
      for (const f of newFiles) {
        const key = `${f.name}::${f.size}::${f.type}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(f);
      }

      if (deduped.length === 0) {
        setAttachmentError(t('ticketDetail.clipboardEmpty', 'Zwischenablage enthält keine unterstützten Inhalte.'));
        setShowAttachmentModal(true);
        return;
      }

      setSelectedFiles(prev => {
        // Auch hier deduplizieren gegen vorhandene Dateien
        const existingKeys = new Set(prev.map(f => `${f.name}::${f.size}::${f.type}`));
        const finalDeduped = deduped.filter(f => {
          const key = `${f.name}::${f.size}::${f.type}`;
          return !existingKeys.has(key);
        });
        return [...prev, ...finalDeduped];
      });
      setShowAttachmentModal(true);
    } catch (_) {
      setAttachmentError(t('ticketDetail.clipboardReadError', 'Fehler beim Lesen aus der Zwischenablage.'));
      setShowAttachmentModal(true);
    }
  };

  const handleUploadAttachments = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploadingAttachments(true);
      await apiService.uploadTicketAttachments(ticket.id, selectedFiles, attachmentDescription);
      
      // Reset form
      setSelectedFiles([]);
      setAttachmentDescription('');
      setShowAttachmentModal(false);
      setHasChanges(true); // Mark as changed
      
      // Reload history to show new attachments
      await loadTicketHistory();
      
      console.log('📎 [UPLOAD] Successfully uploaded attachments');
    } catch (error) {
      console.error('📎 [UPLOAD] Error uploading attachments:', error);
      setError('Fehler beim Hochladen der Dateien');
    } finally {
      setUploadingAttachments(false);
    }
  };

  // #636: Bilder und PDFs öffnen im AnhangViewer. Blättern, Zoom und
  // Tastatur übernimmt der Betrachter selbst.
  const closeImageModal = () => {
    setShowImageModal(false);
    setCurrentImageIndex(-1);
    justClosedAtRef.current = Date.now();
  };

  const handleImageClick = async (ticketId, attachmentId, originalName) => {
    if (Date.now() - (justClosedAtRef.current || 0) < 350) {
      return; // prevent click-through reopen right after close
    }
    const tid = ticketId || currentTicket.id;
    const idx = imageList.findIndex(img => img.attachmentId === attachmentId && (img.ticketId || currentTicket.id) === tid);
    if (idx >= 0) {
      setCurrentImageIndex(idx);
    } else {
      // Fallback: Anhang (noch) nicht in der Liste, einzeln zeigen
      setImageList(prev => [...prev, { ticketId: tid, attachmentId, originalName }]);
      setCurrentImageIndex(imageList.length);
    }
    setShowImageModal(true);
  };

  const handleDownloadAttachment = async (ticketId, attachmentId, originalName) => {
    try {
      const blob = await apiService.downloadTicketAttachment(ticketId, attachmentId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('📎 [DOWNLOAD] Error downloading attachment:', error);
      setError('Fehler beim Herunterladen der Datei');
    }
  };

  const handleDeleteAttachment = async (ticketId, attachmentId, originalName) => {
    try {
      const confirmed = await confirm({ message: t('ticketDetail.confirmDeleteAttachment', 'Diesen Anhang wirklich löschen?'), danger: true });
      if (!confirmed) return;
      await apiService.deleteTicketAttachment(ticketId, attachmentId);
      setHasChanges(true);
      await loadTicketHistory();
    } catch (error) {
      console.error('📎 [DELETE] Error deleting attachment:', error);
      setError(t('ticketDetail.deleteAttachmentError', 'Fehler beim Löschen des Anhangs'));
    }
  };

  // ================ DRAG & DROP FOR ATTACHMENTS ================
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Block drag overlay while certain modals are open
    if (showAddCommentModal || showEditModal || showEditCommentModal || showTransferModal || showCompleteModal) return;
    setIsDragOver(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (showAddCommentModal || showEditModal || showEditCommentModal || showTransferModal || showCompleteModal) return;
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (showAddCommentModal || showEditModal || showEditCommentModal || showTransferModal || showCompleteModal) return;

    const droppedFiles = Array.from(e.dataTransfer?.files || []);
    if (droppedFiles.length === 0) return;

    const validFiles = [];
    for (const file of droppedFiles) {
      if (file.size > 10 * 1024 * 1024) { // 10MB
        setAttachmentError(t('ticketDetail.fileTooLarge', 'Datei "{{name}}" ist zu groß. Maximum sind 10MB.', { name: file.name }));
        setShowAttachmentModal(true);
        return;
      }
      validFiles.push(file);
    }

    setSelectedFiles(validFiles);
    setAttachmentError(null);
    setShowAttachmentModal(true);
  };

  // Globaler Paste-Listener, solange TicketDetail geöffnet ist
  useEffect(() => {
    const onPaste = (ev) => {
      try { handlePasteFromClipboardEvent(ev); } catch (_) {}
    };
    try {
      // Only attach global paste handler when no blocking modal is open
      if (!showAddCommentModal && !showEditModal && !showEditCommentModal && !showTransferModal && !showCompleteModal) {
        window.addEventListener('paste', onPaste);
      }
    } catch (_) {}
    return () => {
      try { window.removeEventListener('paste', onPaste); } catch (_) {}
    };
  }, [showAddCommentModal, showEditModal, showEditCommentModal, showTransferModal, showCompleteModal, handlePasteFromClipboardEvent]);

  // Wenn blockierende Modale geöffnet sind, Drag-Overlay sicherheitshalber ausblenden
  useEffect(() => {
    if (showAddCommentModal || showEditModal || showEditCommentModal || showTransferModal || showCompleteModal) {
      setIsDragOver(false);
    }
  }, [showAddCommentModal, showEditModal, showEditCommentModal, showTransferModal, showCompleteModal]);

  return {
    showAttachmentModal, setShowAttachmentModal, selectedFiles, setSelectedFiles, attachmentDescription, setAttachmentDescription,
    uploadingAttachments, setUploadingAttachments, attachmentError, setAttachmentError, showImageModal, setShowImageModal,
    thumbnailUrls, setThumbnailUrls,
    isDragOver, setIsDragOver, imageList, setImageList, currentImageIndex, setCurrentImageIndex,
    justClosedAtRef, thumbnailUrlsRef, thumbnailRequestedRef, thumbnailObserverRef, handleFileSelect, handlePasteFromClipboardEvent,
    handleUploadAttachments, closeImageModal, handleImageClick,
    handleDownloadAttachment, handleDeleteAttachment, handleDragEnter, handleDragOver, handleDragLeave, handleDrop
  };
}

export default function AnhangDialoge({ anhaenge, formatFileSize }) {
  const { t } = useI18n();
  const {
    showAttachmentModal, setShowAttachmentModal, selectedFiles, setSelectedFiles, attachmentDescription, setAttachmentDescription,
    uploadingAttachments, attachmentError, setAttachmentError, showImageModal,
    imageList, currentImageIndex, handleFileSelect, handlePasteFromClipboardEvent, handleUploadAttachments,
    closeImageModal
  } = anhaenge;
  return (
    <>
        {/* ATTACHMENT UPLOAD MODAL */}
        {showAttachmentModal && (
        <Modal
          open
          onClose={() => { setShowAttachmentModal(false); setSelectedFiles([]); setAttachmentDescription(''); setAttachmentError(null); }}
          title={t('ticketDetail.attach', 'Dateien anhängen')}
          className="attachment-upload-modal"
          maxWidth="600px"
        >
              
              <div className="modal-body">
                <div className="file-upload-section" onPaste={handlePasteFromClipboardEvent}>
                  {attachmentError && (
                    <div className="attachment-error">
                      {attachmentError}
                    </div>
                  )}
                  <div className="file-upload-row">
                    <label htmlFor="file-upload" className="file-upload-label">
                      {t('ticketDetail.selectFilesLabel', 'Dateien auswählen (max. 10MB pro Datei)')}
                    </label>
                  </div>
                  <div className="paste-hint">
                    {t('ticketDetail.pasteHint', 'Tipp: Inhalte können direkt mit Strg+V eingefügt werden.')}
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="file-upload-input"
                    accept="*/*"
                  />
                  
                  {selectedFiles.length > 0 && (
                    <div className="selected-files">
                      <h4>{t('ticketDetail.selectedFiles', 'Ausgewählte Dateien:')}</h4>
                      <ul>
                        {selectedFiles.map((file, index) => (
                          <li key={index} className="file-item">
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">({formatFileSize(file.size)})</span>
                            {file.type.startsWith('image/') && <span className="file-type">🖼️</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <textarea
                    value={attachmentDescription}
                    onChange={(e) => setAttachmentDescription(e.target.value)}
                    placeholder={t('ticketDetail.optionalFilesDescription', 'Optionale Beschreibung für die Dateien...')}
                    rows="3"
                    className="form-textarea"
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAttachmentModal(false);
                    setSelectedFiles([]);
                    setAttachmentDescription('');
                    setAttachmentError(null);
                  }} 
                  className="btn btn-secondary"
                >
                  {t('common.cancel', 'Abbrechen')}
                </button>
                <button 
                  onClick={handleUploadAttachments}
                  disabled={uploadingAttachments || selectedFiles.length === 0}
                  className="btn btn-primary"
                >
                  {uploadingAttachments 
                    ? t('ticketDetail.uploading', 'Hochladen...') 
                    : t('ticketDetail.uploadFilesCount', '{{count}} Datei(en) hochladen', { count: selectedFiles.length })}
                </button>
              </div>
        </Modal>
        )}

        {/* #636: Bilder und PDFs im AnhangViewer */}
        {showImageModal && imageList.length > 0 && (
          <AnhangViewer
            anhaenge={imageList.map(img => ({
              id: `${img.ticketId}-${img.attachmentId}`,
              name: img.originalName,
              typ: img.contentType,
              laden: () => apiService.downloadTicketAttachment(img.ticketId, img.attachmentId),
              downloadUrl: ticketAnhangUrl(img.ticketId, img.attachmentId)
            }))}
            startIndex={Math.max(0, currentImageIndex)}
            onClose={closeImageModal}
          />
        )}
    </>
  );
}
